import type { components } from "@certn/api/generated-schemas/applicant-portal";

/**
 * Hand-written fetch for the unauthenticated invite-opened endpoint.
 *
 * The request body type comes from the generated applicant schema
 * (`InviteOpened`). The POST itself stays a raw fetch: `fetcher.request`
 * waits on an Auth0 access token and can start the redirect before this
 * call finishes.
 *
 * `keepalive` lets a POST that has already passed the CORS preflight finish
 * after that redirect unloads the page. The preflight itself is cancelled
 * with the document, so the caller stores the session marker only after a
 * 2xx and can retry on the next load.
 */

export const INVITE_OPENED_CHANNELS = ["EMAIL", "SMS", "WHATSAPP"] as const;

export type InviteOpenedChannel = (typeof INVITE_OPENED_CHANNELS)[number];

export async function recordInviteOpened(
  domain: string,
  caseId: string,
  body: components["schemas"]["InviteOpened"]
): Promise<void> {
  const path = `/api/internal/applicant/case/${encodeURIComponent(caseId)}/invite-opened/`;
  const url = new URL(path, domain);

  const response = await fetch(url.toString(), {
    method: "POST",
    credentials: "same-origin",
    keepalive: true,
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    throw new Error(
      `Invite opened request failed with status ${response.status}`
    );
  }
}
